import axios from "axios";
import { ENV } from "@/config/env";
import { STORAGE_KEYS } from "@/config/constants";
import { storage } from "@/shared/utils/storage";
import type { UserProfile } from "@/features/auth/types/auth";
import api from "@/shared/services/apiClient";

const BASE_URL = ENV.API_URL;

export const validateRegistrationCode = async (code: string) => {
  try {
    const response = await axios.post(`${BASE_URL}/register/validate-code`, { code });
    return response.data;
  } catch (error: any) {
    throw error.response?.data?.error_code || error.response?.data?.error || "Invalid Code";
  }
};

export const registerUserAction = async (userData: any) => {
  try {
    const response = await axios.post(`${BASE_URL}/register/registerUser`, userData);
    return response.data;
  } catch (error: any) {
    throw error.response?.data?.error_code || error.response?.data?.error || "Registration failed";
  }
};

export const loginUserAction = async (email: string, password: string) => {
  try {
    const response = await axios.post(`${BASE_URL}/login/loginUser`, { email, password });
    if (response.data?.token) {
      const { token, user } = response.data;
      storage.set(STORAGE_KEYS.SECURE_TOKEN, token);
      storage.set(STORAGE_KEYS.USER_ID, user.id.toString());
      storage.setJSON(STORAGE_KEYS.USER_DATA, user);
    }
    return response.data;
  } catch (error: any) {
    const code = error.response?.data?.error_code;
    const message = error.response?.data?.error || "Login failed.";
    throw new Error(code ?? message);
  }
};

export const requestActivationOtp = async (email: string): Promise<void> => {
  await api.post("/login/activate/request-otp", { email: email.trim().toLowerCase() });
};

export const activateAccount = async (
  email: string,
  otp: string,
  password: string,
) => {
  const { data } = await api.post("/login/activate", {
    email: email.trim().toLowerCase(),
    otp,
    password,
  });
  return data;
};

export const forgotPasswordAction = async (email: string) => {
  try {
    const response = await axios.post(`${BASE_URL}/login/forgot-password`, {
      email: email.trim().toLowerCase(),
    });
    return response.data;
  } catch (error: any) {
    const message = error.response?.data?.message || "Failed to request password reset.";
    throw new Error(message);
  }
};

export const updatePasswordAction = async (newPassword: string) => {
  try {
    const secureToken = storage.get(STORAGE_KEYS.SECURE_TOKEN);
    const response = await axios.post(
      `${BASE_URL}/login/updatePassword`,
      { newPassword },
      { headers: { "x-app-identity": secureToken } },
    );
    return response.data;
  } catch (error: any) {
    const code = error.response?.data?.error_code;
    const message = error.response?.data?.message || error.response?.data?.msg || "Connection error";
    throw new Error(code ?? message);
  }
};

export const refreshUserProfile = async (): Promise<UserProfile> => {
  const previous = storage.getJSON<UserProfile>(STORAGE_KEYS.USER_DATA);
  if (!previous) throw new Error("No authenticated profile");
  const settings = await api.get("/profile/bu-settings");
  const fresh = { ...previous, ...settings.data };
  if (previous.userType?.toLowerCase() !== "souser") return fresh;
  const { data } = await api.get("/souser/me");
  return {
    ...fresh,
    firstName: data.first_name, lastName: data.last_name,
    souser_country: data.country, souser_sending_org: data.sending_org,
    souser_primary_bu: data.primary_bu, souser_scope: data.scope,
    announcements_read: data.scope?.valid === true && data.scope?.announcements_read === true,
    announcements_write: data.scope?.valid === true && data.scope?.announcements_write === true,
  };
};

export const deleteAccountAction = async () => {
  try {
    const secureToken = storage.get(STORAGE_KEYS.SECURE_TOKEN);
    const response = await axios.delete(`${BASE_URL}/login/deleteAccount`, {
      headers: { "x-app-identity": secureToken },
    });
    return response.data;
  } catch (error: any) {
    const message = error.response?.data?.msg || error.response?.data?.error || "Failed to delete account";
    throw new Error(message);
  }
};
